import * as Sentry from "@sentry/browser";
import { Button, Checkbox, Col, Descriptions, message, Row, Spin } from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { withRouter } from "react-router-dom";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import CustomerBalanceBreakdown from "../components/CustomerBalanceBreakdown";
import ModalCreator from "../components/ModalCreator";
import {
  CHECK_CUSTOMER_CONTRIBUTION,
} from "../gql/customers";
import { GET_DEPARTMENTS } from "../gql/departments";
import {
  CREATE_ATTACHMENT,
  GET_CATEGORIES,
  GET_FINANCIAL_INSTITUTIONS,
  GET_ID_TYPES,
} from "../gql/general";
import {
  GET_REDEMPTION_CHECKLIST,
  GET_REDEMPTION_PURPOSES,
  GET_REDEMPTION_REQUEST_FOR_EDIT,
  GET_REDEMPTION_SUB_PURPOSES,
  SEARCH_REDEMPTION_REQUEST_FOR_EDIT,
  UPDATE_REDEMPTION_REQUEST,
} from "../gql/redemption-request";
import { ALL_USERS_QUERY } from "../gql/users";
import {
  Account,
  Activity,
  AssociatedEmail,
  AssociatedPhone,
  Attachment,
  BranchMap,
  Category,
  Company,
  Customer,
  Department,
  FinancialInstitution,
  FormFieldConfig,
  Id,
  PaymentAccount,
  PaymentAccountTypesEnum,
  PaymentModeTypesEnum,
  PayoutVerificationFailure,
  Product,
  ProductContribution,
  ProductWithSubscriptionId,
  RedemptionRequestAttachment,
  RedemptionRequestChecklist,
  RedemptionsRequest,
  SelectOptionsInterface,
  Subscription,
  User,
  ValidateAccount,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  filterAccountsByMode,
  filterUndefinedValues,
  formatPayoutAccountAsSelectOptions,
  generateContactTypes,
  generateDisplayErrorMessage,
  generateFirstSelectOption,
  generateNameFromCustomer,
  generateRedemptionTypes,
  getPayoutVerificationError,
  handleValidationErrors,
  injectKey,
  isEmail,
  isTierThreeProduct,
  paymentAccountTypes,
  paymentModeTypes,
  redemptionTypes,
  request,
  responseHandler,
  savedPayoutAccountHiddenFields,
  filterActiveSubscriptions,
} from "../helpers/utilities";
import { otherRedemptionPurposeId } from "./NewRedemptionRequest";
import {
  UPDATE_PAYOUT_ACCOUNT,
  VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
} from "../gql/accounts";
import { ghanaCardRegex, PAYOUT_VALIDATION_THRESHOLD } from "../helpers/constants";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  isLoading: boolean;
  id: string | number;
  redemptionRequest?: RedemptionsRequest;
  products: Product[];
  customerSubscriptions?: Subscription[];
  companySubscriptions?: Subscription[];
  customerPayoutAccounts?: PaymentAccount[];
  companyPayoutAccounts?: PaymentAccount[];
  redemptionPurposes: SelectOptionsInterface[];
  redemptionRequestSubPurposes: SelectOptionsInterface[];
  companies: Company[];
  customers: Customer[];
  redemptionRequestChecklist: RedemptionRequestChecklist[];
  searchTerm: string;
  userSearchTerm: string;
  isSearching: boolean;
  isCreateRedemptionBtnDisabled: boolean;
  selectedChecklist: string[];
  redemptionType: string;
  vestingRuleType: string;
  redemptionRequestAttachments: RedemptionRequestAttachment[];
  attachmentIds: string[];
  attachments: Attachment[];
  selectedCustomer?: Customer;
  selectedCompany?: Company;
  requestorType: string;
  contactType: string;
  redemptionFieldsToHide: string[];
  contactTypeFieldsToHide: string[];
  vestingRuleFieldsToHide: string[];
  taxWaiverFieldsToHide: string[];
  vestingRuleSectionFieldsToHide: string[];
  productsWithSubscriptionId: ProductWithSubscriptionId[];
  selectedProduct?: ProductWithSubscriptionId;
  redemptionsRequests: RedemptionsRequest[];
  showConfirmCreateModal: boolean;
  customerContributions?: ProductContribution[];
  isLoadingContributions: boolean;
  productContribution: ProductContribution | undefined;
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  creatingNewPayoutAccount: boolean;
  payoutAccountFieldsToHide: string[];
  payoutFinancialInstitutionId: string;
  paymentAccountType: string;
  idTypes: Id[];
  bankBranchId: string;

  category: Category | undefined;
  users: User[];
  initialTotalAmountType: string | undefined;
  redemptionRequestPurposeId: string;
  showCancelRequest: boolean;
  manualAllocationFieldsToHide: string[];
  isManualAllocationChecked: boolean;
  isFormerCompany: boolean;
  savePayoutAccount: boolean;
  showAccountVerificationModal: boolean;
  accountNumber?: string;
  accountValidationResponse?: ValidateAccount;
  isVerifyingAccount: boolean;
  institutionOptions: FinancialInstitution[];
  payoutBranches: SelectOptionsInterface[];
  accountName?: string;
  customerModifiedAccounts?: SelectOptionsInterface[];
  selectedPayoutAccount?: string
  paymentMode?: string
  taxWaiverNpraUrl?: string
  vestingRuleUrl?: string
  isCheque: boolean
  unvestedEmployerPreTax?: number,
  unvestedEmployerPostTax?: number,
  unvestedUnassigned?: number,
  isPreTaxDisabled?: boolean
  isPostTaxDisabled?: boolean
  clientContactInformation?: string
  requestorTypeFieldsToHide: string[];
  bypassValidation: boolean
  lastVerificationFailure?: PayoutVerificationFailure;
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
  user: User;
  customerActivityId?: string;
  companyInteractionId?: string;
  customer?: Customer;
  company?: Company;
  companyInteraction?: Activity;
  customerInteraction?: Activity;
  handleSuccess?: () => void;
}

const SEARCH_CUSTOMER = `
query SearchCustomer($q: String!, $page: ID!, $numberPerPage: ID) {
  customerSearch(q: $q, page: $page, numberPerPage: $numberPerPage) {
    count
    results {
      id
      petraId
      firstName
      middleName
      lastName
      activeCustomerPhones {
        id,
        phone {
          id
          number
          type
        }
        isPrimary
      }
      customerEmails {
        id
        isPrimary
        email {
          id
          address
        }
      }
      customerSubscriptions {
        id
        product {
          id
          name
          isActive
          fundCode
          taxWaiverRequired
          productTier {
            id
            name
          }
          productCategory {
            id
            name
          }
          opportunityProspect {
            name
          }
          assetMapId
        }
        productDuration
      }
      customerPayoutAccounts {
        id
        accountNumber
        name
        accountType
        institutionName
        bankBranch {
          id
          name
          financialInstitution {
            id
            name
          }
        }
      }
      customerCompanyAssociations {
        company {
          id
          name
        }
      }
      employers {
        id
        name
        industrySector {
          name
        }
        petraId
        tinNumber
      }
      formerCompanies {
        id
        name
        industrySector {
          name
        }
        petraId
        tinNumber
      }
    }
    errors {
      property
      description
    }
  }
}
`;

const fieldsToBeValidated = [
  "requestDate",
  "valueDate",
  "paidDate",
  "correctInfoDate",
  "redemptionType",
  "customerId",
  "companyId",
  "redemptionRequestPurposeId",
  "redemptionRequestSubPurposeId",
  "redemptionValue",
  "customerSubscriptionId",
  "payoutAccountId",
  "contactType",
  "totalAmountType",
  // "includeVestingRule", // option temporarily removed from the UI for Tier 3 redemptions
  "vestingValue",
  "vestingValueType",
  "vestingRuleUrl",
  "email",
  "phone",
  "idNumber",
  "waiveOffTax",
  "taxWaiverNpraUrl",
  "accountType",
  "name",
  "accountNumber",
  "bankBranchId",
  "subCategoryId",
  "userId",
  "paymentMode",
  "unvestedUnassigned",
  "additionalPurposeDescription",
  "savePayoutAccount",
  "financialInstitutionId",
  "clientContactInformation"
];

const rightFormFieldsToBeValidated = [
  "unvestedUnassigned",
  "isManualAllocation",
  "manualContribution",
  "manualEmployerPreTax",
  "manualEmployerPostTax",
  "manualEmployeePreTax",
  "manualEmployeePostTax",
  "manualWithholdingTax",
  "unvestedEmployerPostTax",
  "unvestedEmployerPreTax",
];

const manualAllocationFields = [
  "manualContribution",
  "manualEmployerPreTax",
  "manualEmployerPostTax",
  "manualEmployeePreTax",
  "manualEmployeePostTax",
  "manualWithholdingTax",
];

class EditRedemptionRequest extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;
  rightFormReference: RefObject<FormInstance>;
  attachmentsReference: RefObject<FormInstance>;
  checklistReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      redemptionRequest: undefined,
      id: "",
      products: [],
      customerSubscriptions: [],
      customerPayoutAccounts: [],
      companySubscriptions: [],
      companies: [],
      customers: [],
      redemptionPurposes: [],
      redemptionRequestAttachments: [],
      redemptionRequestSubPurposes: [],
      redemptionRequestChecklist: [],
      searchTerm: "",
      userSearchTerm: "",
      isSearching: false,
      isCreateRedemptionBtnDisabled: true,
      selectedChecklist: [],
      redemptionFieldsToHide: [
        "redemptionRequestSubPurposeId",
        "additionalPurposeDescription",
      ],
      contactTypeFieldsToHide: ["phone", "email"],
      vestingRuleSectionFieldsToHide: ["includeVestingRule"],
      vestingRuleFieldsToHide: [
        "vestingValue",
        "vestingValueType",
        "vestingRuleUrl",
      ],
      taxWaiverFieldsToHide: ["taxWaiverNpraUrl"],
      contactType: "",
      redemptionType: "",
      vestingRuleType: "",
      requestorType: "",
      selectedCustomer: undefined,
      attachmentIds: [],
      attachments: [],
      productsWithSubscriptionId: [],
      selectedProduct: undefined,
      redemptionsRequests: [],
      showConfirmCreateModal: false,
      customerContributions: [],
      isLoadingContributions: false,
      productContribution: undefined,
      financialInstitutions: [],
      branchMap: {},
      creatingNewPayoutAccount: false,
      payoutAccountFieldsToHide: [],
      payoutFinancialInstitutionId: "",
      paymentAccountType: "",
      idTypes: [],
      bankBranchId: "",
      category: undefined,
      users: [],
      initialTotalAmountType: undefined,
      redemptionRequestPurposeId: "",
      showCancelRequest: false,
      manualAllocationFieldsToHide: manualAllocationFields,
      isManualAllocationChecked: false,
      isFormerCompany: false,
      savePayoutAccount: false,
      showAccountVerificationModal: false,
      accountNumber: "",
      isVerifyingAccount: false,
      institutionOptions: [],
      payoutBranches: [],
      isCheque: false,
      requestorTypeFieldsToHide: [""],
      bypassValidation: false,
      lastVerificationFailure: undefined,
    };

    this.reference = React.createRef<FormInstance>();
    this.rightFormReference = React.createRef<FormInstance>();
    this.attachmentsReference = React.createRef<FormInstance>();
    this.checklistReference = React.createRef<FormInstance>();
    this.cancelRequest = this.cancelRequest.bind(this);
  }

  componentDidMount(): void {
    this.setState(
      { id: this.props.match.params.id || 0, isLoading: true },
      (): void => {
        this.getFinancialInstitutionals();
        this.getIdTypes();
        this.getCategories();
        this.getRedemptionRequest();
      },
    );
  }

  handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const accountNumber = event.target.value;

    this.setState({ accountNumber });

    if (accountNumber.trim() === "") {
      this.setState({ isCheque: true });
    } else {
      this.setState({ isCheque: false });
    }
  };

  toggleModal = (): void => {
    this.setState({
      showConfirmCreateModal: false,
    });
  };

  toggleSavePayoutAccount = (checkedValue: boolean) => {
    this.setState({
      savePayoutAccount: checkedValue,
    });
  };

  toggleConfirmCreateModal = (): void => {
    const { redemptionsRequests, selectedProduct } = this.state;

    const pendingRedemption = redemptionsRequests.find(
      (redemptionsRequest: RedemptionsRequest): boolean => {
        return (
          redemptionsRequest.state === "pending" &&
          redemptionsRequest.customerSubscription?.id ===
          selectedProduct?.subscriptionId
        );
      },
    );

    if (pendingRedemption) {
      this.setState({
        showConfirmCreateModal: true,
        isLoading: false,
      });
    } else {
      this.updateRedemptionRequest();
    }
  };

  getRedemptionRequest(): void {
    const { id } = this.state;
    interface Response {
      redemptionRequest: RedemptionsRequest;
    }
    const query = GET_REDEMPTION_REQUEST_FOR_EDIT;
    this.setState({ isLoading: true });
    request({
      query,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        const contactType = data?.data?.redemptionRequest?.contactInformation
          ? isEmail(data?.data?.redemptionRequest?.contactInformation || "")
            ? "email"
            : "phone"
          : "phone";
        this.handleContactTypeChange(contactType);
        const redemptionRequest = data?.data?.redemptionRequest;
        const selectedCustomer = redemptionRequest?.customer;
        const customerPayoutAccountsInSubscription =
          selectedCustomer?.customerSubscriptions
            ?.map((item) => {
              return item?.customerPayoutAccounts ?? [];
            })
            ?.reduce(
              (
                previousValue: PaymentAccount[],
                currentValue: PaymentAccount[],
              ) => {
                return previousValue.concat(currentValue);
              },
              [],
            ) ?? [];
        this.setState(
          {
            redemptionRequest,
            initialTotalAmountType: redemptionRequest?.totalAmountType,
            selectedCompany: redemptionRequest?.company,
            customerSubscriptions: selectedCustomer?.customerSubscriptions,
            customerPayoutAccounts: customerPayoutAccountsInSubscription,
            redemptionType: redemptionRequest?.redemptionType,
            attachments: redemptionRequest.redemptionRequestAttachments.map(
              (redemptionRequestAttachment) =>
                redemptionRequestAttachment.attachment,
            ),
            redemptionRequestAttachments:
              redemptionRequest.redemptionRequestAttachments,
            userSearchTerm: redemptionRequest?.user?.name ?? "",
            isFormerCompany: redemptionRequest?.isFormerCompany ?? false,
            accountNumber: redemptionRequest?.payoutAccount?.accountNumber,
            accountName: redemptionRequest?.payoutAccount?.name ?? "",
            paymentMode: redemptionRequest?.paymentMode,
            selectedPayoutAccount: redemptionRequest?.payoutAccount?.id ?? "",
            unvestedEmployerPreTax: redemptionRequest?.unvestedEmployerPreTax,
            unvestedEmployerPostTax: redemptionRequest?.unvestedEmployerPostTax,
            unvestedUnassigned: redemptionRequest?.unvestedUnassigned,
            isPreTaxDisabled: !!redemptionRequest?.unvestedEmployerPreTax,
            isPostTaxDisabled: !!redemptionRequest?.unvestedEmployerPostTax,
          },
          () => {
            let payoutAccountFieldsToHide: string[] = [];
            let isCheque: boolean
            if (this.state.redemptionRequest?.clientContactInformation === "customer") {
              this.setState({ requestorTypeFieldsToHide: ["clientContactInformation"] })
            }
            if (this.state.redemptionRequest?.payoutAccount) {
              payoutAccountFieldsToHide = savedPayoutAccountHiddenFields(
                this.state.redemptionRequest.payoutAccount as PaymentAccount,
                { showThirdParty: true },
              );
            } else {
              payoutAccountFieldsToHide = ["name", "bankBranchId"];
            }

            if (this.state.paymentMode === "cheque") {
              isCheque = true;
            } else isCheque = false;

            this.setState({
              payoutAccountFieldsToHide,
              isCheque,
            });
          },
        );
        this.setInitialSelectedCustomerOrCompany(
          redemptionRequest.customer,
          undefined,
        );

        this.handleCustomerSelectOnPageLoad(redemptionRequest.customer);
        this.handleProductSelect(
          redemptionRequest?.customerSubscription?.product?.id || "",
        );
        this.handleRedemptionPurposeChange(
          redemptionRequest?.redemptionRequestPurpose?.id || "",
        );
        this.changeIsManualAllocation(
          redemptionRequest?.isManualAllocation ?? false,
        );
        this.handleTaxWaiveChange(
          ![null, "", undefined].includes(redemptionRequest?.taxWaiverNpraUrl)
            ? "yes"
            : "no",
        );
        this.getAllUsers();
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  }

  getRedemptionPurposes = (): void => {
    const { selectedProduct } = this.state;
    interface Response {
      data: {
        redemptionRequestPurposes: SelectOptionsInterface[];
      };
    }
    this.setState({ isSearching: true });
    request({
      query: GET_REDEMPTION_PURPOSES,
      variables: {
        productId: selectedProduct?.id,
      },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          redemptionPurposes: data.data.redemptionRequestPurposes,
        });
      })
      .catch((error) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isSearching: false });
      });
  };

  getRequestDate = (): moment.Moment | undefined => {
    if (this.state.redemptionRequest?.requestDate)
      return moment(this.state.redemptionRequest?.requestDate);
    let requestDate;
    if (this.props.customerInteraction) {
      requestDate = moment(this.props.customerInteraction.activityDate);
    } else if (this.props.companyInteraction) {
      requestDate = moment(this.props.companyInteraction.activityDate);
    }

    return requestDate;
  };

  setRef = (
    name: "reference" | "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  redirectToAllRedemptionsPage = (): void => {
    this.props.history.push(
      `/redemption-requests/${this.props.match.params.id}`,
    );
  };

  redirectToViewRedemptionPage = (id: string): void => {
    this.props.history.push(`/redemption-requests/${id}`);
  };

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ searchTerm }, async () => {
      await this.triggerDelayedSearch();
    });
  };

  handleCustomerSelect = async (id: string): Promise<void> => {
    const selectedCustomer = this.state.customers.find(
      (customer: Customer): boolean => customer.id === id,
    );

    this.setState({
      selectedCustomer,
      selectedCompany: undefined,
      customerSubscriptions: selectedCustomer?.customerSubscriptions,
      customerPayoutAccounts: selectedCustomer?.customerPayoutAccounts,
    });
  };

  handleCustomerSelectOnPageLoad = async (
    customer: Customer | undefined,
  ): Promise<void> => {
    const selectedCustomer = customer;

    this.setState({
      selectedCustomer,
      selectedCompany: undefined,
      customerSubscriptions: selectedCustomer?.customerSubscriptions,
      customerPayoutAccounts: selectedCustomer?.customerPayoutAccounts,
    });
  };

  mapProductsToSubscriptions = (
    subscriptions: Subscription[] | undefined,
  ): void => {
    const productsWithSubscriptionId: ProductWithSubscriptionId[] = [];
    const products: Product[] = [];
    filterActiveSubscriptions(subscriptions).map((subs: Subscription) => {
      productsWithSubscriptionId.push({
        ...subs.product,
        subscriptionId: subs.id,
        productDuration: subs.productDuration,
        customerPayoutAccounts: subs.customerPayoutAccounts,
      });
      products.push({ ...subs.product });
    });
    this.setState({ products, productsWithSubscriptionId });
  };

  handleCompanySelect = async (id: string): Promise<void> => {
    const selectedCompany = this.state.selectedCustomer?.employers.find(
      (company: Company): boolean => company.id === id,
    );

    this.setState({
      selectedCompany,
    });
  };

  handleRequestTypeSelect = async (id: string): Promise<void> => {
    this.setState({
      initialTotalAmountType: id === "1" ? "net" : undefined,
    });
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    await this.triggerCustomerSearch();
  }, DEBOUNCE_TIME);

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  handleContactTypeChange = (contactType: string): void => {
    let contactTypeFieldsToHide: string[] = [];

    switch (contactType) {
      case "phone":
        contactTypeFieldsToHide = ["email"];
        break;
      case "email":
        contactTypeFieldsToHide = ["phone"];
        break;
      default:
        contactTypeFieldsToHide = [];
        break;
    }

    this.setState({
      contactType,
      contactTypeFieldsToHide,
    });
  };

  getPrimaryPhone = (): string | undefined => {
    let phones: AssociatedPhone[] = [];

    if (this.state.selectedCustomer) {
      phones = this.state.selectedCustomer.activeCustomerPhones;
    }

    if (this.state.selectedCompany) {
      phones = this.state.selectedCompany.companyPhones;
    }

    const phone = phones?.find((p: AssociatedPhone): boolean => p.isPrimary)
      ?.phone.number;

    return phone;
  };

  getPrimaryEmail = (): string | undefined => {
    let emails: AssociatedEmail[] = [];

    if (this.state.selectedCustomer) {
      emails = this.state.selectedCustomer.customerEmails;
    }

    if (this.state.selectedCompany) {
      emails = this.state.selectedCompany.companyEmails;
    }

    const email = emails?.find((e: AssociatedEmail): boolean => e.isPrimary)
      ?.email.address;

    this.reference.current?.setFieldsValue({ email });

    return email;
  };

  triggerCustomerSearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1, numberPerPage: 50 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  setInitialSelectedCustomerOrCompany = (
    customer: Customer | undefined,
    company: Company | undefined,
  ): void => {
    if (company || company !== undefined) {
      this.setState({ selectedCompany: company });
    } else {
      this.setState({
        selectedCustomer: customer,
        customerPayoutAccounts: customer?.customerPayoutAccounts,
        customerSubscriptions: customer?.customerSubscriptions,
      });
      this.mapProductsToSubscriptions(customer?.customerSubscriptions);
    }
  };

  toggleRedemptionBtnVisible = (values: CheckboxValueType[]): void => {
    const { redemptionRequestChecklist } = this.state;
    const checklist = values as string[];
    const requiredFields = redemptionRequestChecklist.filter(
      (checklist) => checklist.isRequired,
    );
    const isAllChecked = requiredFields.every((field) => {
      return checklist.includes(field.name);
    });
    this.setState({
      isCreateRedemptionBtnDisabled: !isAllChecked,
      selectedChecklist: checklist,
    });
  };

  searchRedemptionRequests = async (): Promise<void> => {
    const { selectedCustomer } = this.state;
    interface Response {
      count: number;
      results: RedemptionsRequest[];
    }

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_REDEMPTION_REQUEST_FOR_EDIT,
        variables: {
          q:
            selectedCustomer?.petraId ??
            this.state.redemptionRequest?.customer?.id,
          page: 1,
        },
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "redemptionRequestSearch",
      );

      if (handled) {
        message.error("Failed To Load Redemptions");
      } else {
        this.setState({
          isLoading: false,
          redemptionsRequests: mutation.results,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  resolveVerificationDetails = async (): Promise<
    { financialInstitutionId: string; accountNumber: string } | undefined
  > => {
    const saved = this.getSelectedSavedPayoutAccount();

    if (saved) {
      return {
        financialInstitutionId:
          (saved.payoutAccount?.bankBranch ?? saved.bankBranch)
            ?.financialInstitution?.id ?? "",
        accountNumber: saved.accountNumber,
      };
    }

    if (!this.reference.current) return undefined;

    try {
      return await this.reference.current.validateFields([
        "financialInstitutionId",
        "accountNumber",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return undefined;
    }
  };

  verifyAccount = async (): Promise<void> => {
    interface Response {
      data: {
        validatePayoutAccountFuzzyMatch: ValidateAccount;
      };
    }

    const data = await this.resolveVerificationDetails();
    if (!data) return;

    this.setState({ isLoading: true });

    try {
      const result = await request({
        query: VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
        variables: { ...data, customerId: this.state.selectedCustomer?.id },
      });

      const response = (result as AxiosResponse<Response>).data.data
        .validatePayoutAccountFuzzyMatch;
      const { status, score, name } = response;

      if (!status) {
        this.setState({
          accountValidationResponse: undefined,
          lastVerificationFailure: undefined,
        });
        message.error(response.message || "Error verifying account number");
        return;
      }

      if (score >= PAYOUT_VALIDATION_THRESHOLD || this.state.bypassValidation) {
        this.setState({
          accountValidationResponse: response,
          lastVerificationFailure: undefined,
        });
        message.success("Account verified successfully");
        message.success(
          `Account name: ${name} (Match Score: ${Math.round(score)}%)`,
        );
        await this.persistPayoutAccountVerification(score);
        return;
      }

      this.setState({
        accountValidationResponse: undefined,
        lastVerificationFailure: { bankAccountName: name, score },
      });
      message.error(
        `Customer name and Bank account name match too low: ${Math.round(
          score,
        )}%. Must be above ${PAYOUT_VALIDATION_THRESHOLD}.`,
      );
      message.success(`Bank account name: ${name}`);
    } catch (error) {
      message.error(
        `Error verifying account number - ${generateDisplayErrorMessage(error)}`,
      );
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  persistPayoutAccountVerification = async (score: number): Promise<void> => {
    const account = this.getSelectedSavedPayoutAccount();
    const { selectedCustomer, selectedProduct } = this.state;

    if (!account) return;
    if (account.isPayoutAccountVerified) return;
    if (score < PAYOUT_VALIDATION_THRESHOLD) return;
    if (!selectedCustomer?.id || !selectedProduct?.subscriptionId) return;

    try {
      await request({
        query: UPDATE_PAYOUT_ACCOUNT,
        variables: {
          id: account.id,
          accountNumber: account.accountNumber,
          accountType: account.accountType,
          name: account.name,
          bankBranchId: (account.payoutAccount?.bankBranch ?? account.bankBranch)
            ?.id,
          customerId: selectedCustomer.id,
          customerSubscriptionId: selectedProduct.subscriptionId,
          isPayoutAccountVerified: true,
        },
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  updateRedemptionRequest = async (): Promise<void> => {
    const {
      accountNumber,
      accountValidationResponse,
      accountName,
      bankBranchId,
      taxWaiverNpraUrl,
      vestingRuleUrl,
      unvestedEmployerPostTax,
      unvestedEmployerPreTax,
      unvestedUnassigned,
    } = this.state;

    interface Response {
      redemptionRequest: RedemptionsRequest;
    }

    if (!this.reference.current) return;

    let data;
    let rightFormData;

    const shouldRedirect = !this.props.customer;

    try {
      const modifiedfieldsToBeValidated =
        this.state.paymentAccountType === "cheque"
          ? fieldsToBeValidated.filter((item) => item !== "bankBranchId")
          : fieldsToBeValidated;
      data = await this.reference.current.validateFields(
        modifiedfieldsToBeValidated,
      );

      rightFormData = await this.rightFormReference.current?.validateFields(
        rightFormFieldsToBeValidated,
      );

      if (Object.keys(filterUndefinedValues(rightFormData)).length > 0) {
        data = {
          ...data,
          ...rightFormData,
          unvestedEmployerPostTax,
          unvestedEmployerPreTax,
          unvestedUnassigned,
          isManualAllocation: this.state.isManualAllocationChecked,
        };

        data = Object.fromEntries(
          Object.entries(data).filter(([_, value]) => value !== undefined),
        );
      }

      if (this.state.bankBranchId) {
        data["bankBranchId"] = /^\d+$/.test("this.state.bankBranchId")
          ? this.state.bankBranchId
          : this.state.redemptionRequest?.companyPayoutAccount?.bankBranch?.id;
      }
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const verificationError = getPayoutVerificationError({
      selectedAccount: this.getSelectedSavedPayoutAccount(),
      hasPassingValidation: !!this.state.accountValidationResponse,
      lastVerificationFailure: this.state.lastVerificationFailure,
      bypassValidation: this.state.bypassValidation,
      clientName: generateNameFromCustomer(this.state.selectedCustomer),
      threshold: PAYOUT_VALIDATION_THRESHOLD,
    });

    if (verificationError) {
      message.error(verificationError);
      return;
    }

    this.setState({ isLoading: true, showConfirmCreateModal: false });

    let variables = {
      ...data,
      id: this.state.redemptionRequest?.id,
      isFormerCompany: this.state.isFormerCompany,
      redemptionType: this.state?.redemptionType,
      redemptionRequestReason: this.state.redemptionRequestPurposeId,
      customerSubscriptionId: this.state.selectedProduct?.subscriptionId,
      savePayoutAccount: this.state.savePayoutAccount,
      payoutFinancialInstitutionId:
        this.state.payoutFinancialInstitutionId ??
        this.state.redemptionRequest?.id,
      newPayoutAccount: data.payoutAccountId === "new_account" ? true : false,
      contactInformation: data[this.state.contactType],
      waiveOffTax: data.waiveOffTax === "yes" ? true : false,
      checklist: this.state.selectedChecklist,
      accountNumber,
      name: accountValidationResponse?.name || accountName,
      bankBranchId,
      idTypeId: "5", //Ghana Card
      taxWaiverNpraUrl,
      vestingRuleUrl,
    };

    // "Override Vesting Rule?" option temporarily removed from the UI for Tier 3 redemptions - default to "No"
    variables = {
      ...variables,
      includeVestingRule: false,
    };

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_REDEMPTION_REQUEST,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateRedemptionRequest",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        this.reference.current.resetFields();
        message.success("Redemption request updated successfully");
        shouldRedirect &&
          this.redirectToViewRedemptionPage(mutation.redemptionRequest.id);
        this.props.handleSuccess && this.props.handleSuccess();
      }
    } catch (error) {
      message.error(`failed to create redemption request - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState((state) => ({
        attachmentIds: [...state.attachmentIds, mutation.attachment.id],
        attachments: [...state.attachments, mutation.attachment],
      }));
      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  removeAttachment = (attachmentId: string): void => {
    this.setState((state) => ({
      attachments: state.attachments.filter(
        (attachment) => attachment.id !== attachmentId,
      ),
      attachmentIds: state.attachmentIds.filter((id) => id !== attachmentId),
    }));
  };

  handleRedemptionTypeChange = (redemptionType: string): void => {
    let type = "";

    switch (redemptionType) {
      case "percentage":
        type = "percentage";
        break;
      case "amount":
        type = "amount";
        break;
      default:
        break;
    }

    this.setState({
      redemptionType: type,
    });
  };

  handleVestingRuleTypeChange = (vestingRuleType: string): void => {
    let type = "";

    switch (vestingRuleType) {
      case "percentage":
        type = "Percentage";
        break;
      case "amount":
        type = "Amount";
        break;
      default:
        break;
    }

    this.setState({
      vestingRuleType: type,
    });
  };

  handleProductSelect = (id: string): void => {
    const { customerContributions, redemptionRequest } = this.state;

    const product = this.state.productsWithSubscriptionId.find(
      (product) => product.id === id,
    );

    if (
      isTierThreeProduct(product) &&
      product?.opportunityProspect?.name === "Company"
    ) {
      this.setState({ vestingRuleSectionFieldsToHide: [] });
    } else {
      this.setState({ vestingRuleSectionFieldsToHide: ["includeVestingRule"] });
    }

    const productContribution = customerContributions?.find(
      (contribution: ProductContribution) =>
        contribution.fundcode === product?.fundCode,
    );

    this.setState({ selectedProduct: product, productContribution }, () => {
      let customerModifiedAccounts: SelectOptionsInterface[] = [];
      const redemptionRequestPayoutAccount = redemptionRequest?.payoutAccount;
      const customerPayoutAccounts = this.state.selectedProduct?.customerPayoutAccounts ?? [];

      if (redemptionRequestPayoutAccount || customerPayoutAccounts.length > 0) {
        const combinedAccounts: Account[] = [];

        if (redemptionRequestPayoutAccount) {
          combinedAccounts.push(redemptionRequestPayoutAccount);
        }

        if (customerPayoutAccounts.length > 0) {
          combinedAccounts.push(
            ...customerPayoutAccounts.filter(
              (account): account is PaymentAccount => !!account,
            ),
          );
        }

        customerModifiedAccounts =
          formatPayoutAccountAsSelectOptions(combinedAccounts);
      }

      if (customerModifiedAccounts.length === 0) {
        customerModifiedAccounts = [
          { id: "new_account", name: "New Redemption Payout Account" },
        ];
      }
      this.setState({
        customerModifiedAccounts,
      });
      this.getRedemptionPurposes();
    });
  };

  handlePaymentModeSelect = (paymentMode: string) => {
    const mode = paymentMode as PaymentModeTypesEnum;
    const filteredAccounts = filterAccountsByMode(
      this.state.selectedProduct?.customerPayoutAccounts ?? [],
      mode,
    );
    const customerModifiedAccounts =
      formatPayoutAccountAsSelectOptions(filteredAccounts);

    this.setState({ customerModifiedAccounts, paymentMode });

    if (paymentMode === "cheque") {
      this.setState({ isCheque: true });
    } else this.setState({ isCheque: false });
  };

  getSelectedSavedPayoutAccountById = (
    id: string | undefined,
  ): PaymentAccount | undefined => {
    if (!id || id === "new_account") return undefined;

    const { selectedProduct, redemptionRequest } = this.state;
    const candidates: PaymentAccount[] = [
      ...(selectedProduct?.customerPayoutAccounts ?? []),
    ];
    if (redemptionRequest?.payoutAccount) {
      candidates.push(redemptionRequest.payoutAccount as PaymentAccount);
    }

    return candidates.find(
      (account) => account.payoutAccount?.id === id || account.id === id,
    );
  };

  getSelectedSavedPayoutAccount = (): PaymentAccount | undefined =>
    this.getSelectedSavedPayoutAccountById(this.state.selectedPayoutAccount);

  handlePayoutAccountSelect = (id: string): void => {
    let selectedPayoutAccount = id;
    let payoutAccountFieldsToHide: string[] = [];
    let creatingNewPayoutAccount = false;
    let accountNumber = "";
    let accountName = "";
    if (id === "new_account") {
      payoutAccountFieldsToHide = ["name", "bankBranchId"];
      creatingNewPayoutAccount = true;
      accountNumber = "";
      accountName = "";
    } else {
      const account = this.getSelectedSavedPayoutAccountById(id);
      payoutAccountFieldsToHide = savedPayoutAccountHiddenFields(account, {
        showThirdParty: true,
      });
      creatingNewPayoutAccount = false;
    }

    this.setState({
      payoutAccountFieldsToHide,
      creatingNewPayoutAccount,
      selectedPayoutAccount,
      accountNumber,
      accountName,
      accountValidationResponse: undefined,
      lastVerificationFailure: undefined,
    });
  };

  handleRedemptionPurposeChange = async (id: string) => {
    let newRedemptionFieldsToHide = this.state.redemptionFieldsToHide;

    return Promise.all([
      this.getRedemptionChecklist(id),
      this.getRedemptionRequestSubPurposes(id),
    ]).then(() => {
      if (id === otherRedemptionPurposeId.toString()) {
        newRedemptionFieldsToHide = this.state.redemptionFieldsToHide.filter(
          (field) => field !== "additionalPurposeDescription",
        );
      } else {
        newRedemptionFieldsToHide = [
          ...this.state.redemptionFieldsToHide,
          "additionalPurposeDescription",
        ];
      }

      this.setState({
        redemptionRequestPurposeId: id,
        redemptionFieldsToHide: newRedemptionFieldsToHide,
      });
    });
  };

  getRedemptionChecklist = async (id: string): Promise<void> => {
    const { selectedProduct, redemptionRequest } = this.state;
    interface Response {
      data: {
        redemptionRequestChecklist: RedemptionRequestChecklist[];
      };
    }

    const currentChecklist = redemptionRequest?.additionalData?.[
      "checklist"
    ] as unknown as string[];

    this.setState({ isSearching: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_REDEMPTION_CHECKLIST,
        variables: {
          productId: selectedProduct?.id,
          redemptionRequestPurposeId: id,
        },
      });

      const redemptionRequestChecklist =
        response.data.data.redemptionRequestChecklist;

      const requiredFields = redemptionRequestChecklist.filter(
        (checklist) => checklist.isRequired,
      );
      const isAllChecked = requiredFields.every((field) => {
        return currentChecklist.includes(field.name);
      });

      this.setState({
        redemptionRequestChecklist: redemptionRequestChecklist,
        selectedChecklist: currentChecklist,
        isCreateRedemptionBtnDisabled: !isAllChecked,
      });
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  getRedemptionRequestSubPurposes = async (id: string) => {
    const { selectedProduct } = this.state;
    interface Response {
      data: {
        redemptionRequestSubPurposes: SelectOptionsInterface[];
      };
    }

    this.setState({ isSearching: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_REDEMPTION_SUB_PURPOSES,
        variables: {
          productId: selectedProduct?.id,
          redemptionRequestPurposeId: id,
        },
      });

      const data = response.data.data.redemptionRequestSubPurposes;
      const fields = data.length > 0 ? [] : ["redemptionRequestSubPurposeId"];

      this.setState({
        redemptionRequestSubPurposes: data,
        redemptionFieldsToHide: fields,
      });
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  generateRedemptionText() {
    const redemptionType = this.state.redemptionType;
    if (redemptionType === "") return "Redemption Value";
    return redemptionType === "amount"
      ? "Redemption Amount"
      : "Redemption Percentage";
  }

  generateVestingRuleText() {
    const vestingType = this.state.vestingRuleType;
    if (vestingType === "") return "Vesting Value";
    return vestingType === "Amount" ? "Vesting Amount" : "Vesting Percentage";
  }

  handleVestingRuleChange = (value: string) => {
    if (value === "yes") {
      this.setState({ vestingRuleFieldsToHide: [] });
    } else {
      this.setState({
        vestingRuleFieldsToHide: [
          "vestingValue",
          "vestingValueType",
          "vestingRuleUrl",
        ],
      });
    }
  };

  generateBranchMap = async (): Promise<void> => {
    const branchMap: BranchMap = {};

    this.state.financialInstitutions.forEach(
      (institution: FinancialInstitution): void => {
        branchMap[institution.id] = institution.bankBranches;
      },
    );

    this.setState({ branchMap });
  };

  handlePayoutFinancialInstitutionChange = async (
    payoutFinancialInstitutionId: string,
  ): Promise<void> => {
    this.setState({
      payoutFinancialInstitutionId,
      bankBranchId: this.state.branchMap[payoutFinancialInstitutionId][0].id,
      payoutBranches: this.state.branchMap[payoutFinancialInstitutionId],
    });
  };

  handleBypassValidationChange = async (checkedValue: boolean) => {
    this.setState({ bypassValidation: checkedValue });
  };

  handleAccountTypeChange = (): void => {
    this.setState({
      accountNumber: "",
      accountValidationResponse: undefined,
      payoutFinancialInstitutionId: "",
    });
  };

  toggleAccountVerificationModal = () => {
    this.setState((prevState) => ({
      showAccountVerificationModal: !prevState.showAccountVerificationModal,
    }));
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handlePaymentTypeChange = (paymentAccountType: string): void => {
    let payoutAccountFieldsToHide: string[] = [];
    let bankBranchId: string;
    if (paymentAccountType === "mm_account") {
      payoutAccountFieldsToHide = ["bankAccountType", "bankBranchId"];
      bankBranchId = "";
    } else if (paymentAccountType === "cheque") {
      payoutAccountFieldsToHide = [
        "accountNumber",
        "bankAccountType",
        "bankBranchId",
        "financialInstitutionId",
        "savePayoutAccount",
      ];
      bankBranchId =
        this.state.branchMap[
          this.state.financialInstitutions.filter(
            (institution: FinancialInstitution): boolean => {
              return institution.institutionType === "cheque";
            },
          )[0].id
        ][0].id;
    } else {
      payoutAccountFieldsToHide = [];
      bankBranchId = "";
    }
    this.setState({
      paymentAccountType,
      payoutAccountFieldsToHide,
      bankBranchId,
      accountNumber: "",
      accountValidationResponse: undefined,
      payoutFinancialInstitutionId: "",
    });
  };

  handlePaymentTypeSelect = (paymentAccountType: string): void => {
    let financialInstitutions: FinancialInstitution[] = [];
    let payoutAccountFieldsToHide: string[] =
      this.state.payoutAccountFieldsToHide.slice();
    if (paymentAccountType === "mm_account") {
      financialInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else {
      financialInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    }
    if (paymentAccountType === "cheque") {
      payoutAccountFieldsToHide = payoutAccountFieldsToHide.filter(
        (field) => field !== "name",
      );
      payoutAccountFieldsToHide.push("bankBranchId");
    } else {
      payoutAccountFieldsToHide = payoutAccountFieldsToHide.filter(
        (field) => field !== "bankBranchId",
      );
      payoutAccountFieldsToHide.push("name");
    }

    this.setState({
      institutionOptions: financialInstitutions,
      payoutAccountFieldsToHide,
      showAccountVerificationModal: paymentAccountType !== "cheque",
    });
  };

  handleBankBranchChange = (bankBranchId: string) => {
    this.setState({ bankBranchId });
  };

  handleAccountNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ accountName: event.target.value });
  };

  getFinancialInstitutionals = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
        isLoading: false,
      });

      this.generateBranchMap();
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  getIdTypes = async (): Promise<void> => {
    interface Response {
      data: {
        idTypes: Id[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_ID_TYPES,
      });

      this.setState({
        idTypes: data.idTypes,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  getDepartments = async (): Promise<Department[]> => {
    interface Response {
      data: {
        departments: Department[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_DEPARTMENTS,
      });

      return data.departments;
    } catch (error) {
      Sentry.captureException(error);
    }
    return [];
  };

  getAllUsers = (): void => {
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  };

  getCategories = async (): Promise<void> => {
    interface Response {
      data: {
        categories: Category[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const departments = await this.getDepartments();

      if (departments.length > 0) {
        const clDepartmet = departments.find(
          (department: Department): boolean => {
            return (
              department.name.toLowerCase() === "client service" ||
              department.name.toLowerCase() === "client services"
            );
          },
        );

        const {
          data: { data },
        }: AxiosResponse<Response> = await request({
          query: GET_CATEGORIES,
          variables: {
            departmentId: clDepartmet?.id,
          },
        });
        const category = data.categories.find((category: Category): boolean => {
          return category.name.toLowerCase() === "redemptions and transfers";
        });
        this.setState({
          category: category,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  handleTaxWaiveChange = async (value: string) => {
    const canWaiveTax = value === "yes";

    this.setState((prevState) => ({
      taxWaiverFieldsToHide: canWaiveTax
        ? prevState.taxWaiverFieldsToHide.filter((item) => item !== "taxWaiverNpraUrl")
        : ["taxWaiverNpraUrl"],
    }));

    const { selectedCustomer } = this.state;
    interface Response {
      data: {
        checkCustomerFirstContribution: boolean;
      };
    }

    this.setState({ isSearching: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: CHECK_CUSTOMER_CONTRIBUTION,
        variables: {
          customerId: selectedCustomer?.id,
          waiveOffTax: canWaiveTax,
        },
      });

      const hasMaturedContribution =
        response.data.data.checkCustomerFirstContribution;

      if (!hasMaturedContribution) {
        this.setState({
          taxWaiverFieldsToHide: [],
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  renderLabel(required: boolean, name: string) {
    return (
      <label>
        {required ? (
          <span>
            <span style={{ color: "red" }}>*</span> {name}
          </span>
        ) : (
          name
        )}
      </label>
    );
  }

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  toggleCancelModal = async () => {
    this.setState({ showCancelRequest: !this.state.showCancelRequest });
  };

  cancelRequest = async () => {
    this.props.history.goBack();
  };

  changeIsManualAllocation = async (checkedValue: boolean) => {
    this.setState({
      manualAllocationFieldsToHide: checkedValue ? [] : manualAllocationFields,
      isManualAllocationChecked: checkedValue,
    });
  };

  changeIsFormerCompany = async (checkedValue: boolean) => {
    this.setState(
      {
        isFormerCompany: checkedValue,
      },
      () => {
        this.reference.current &&
          this.reference.current.setFieldsValue({ companyId: undefined });
      },
    );
  };

  handleInputChange = (field: keyof State, value: number | null) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        [field]: value !== null && value !== undefined ? value : undefined,
      }),
      () => {
        const { unvestedEmployerPreTax, unvestedEmployerPostTax } = this.state;
        const preTaxValue = unvestedEmployerPreTax || 0;
        const postTaxValue = unvestedEmployerPostTax || 0;

        if (
          unvestedEmployerPreTax !== undefined &&
          unvestedEmployerPostTax !== undefined
        ) {
          this.setState({
            unvestedUnassigned: preTaxValue + postTaxValue,
          });
        } else {
          this.setState({
            unvestedUnassigned: undefined,
          });
        }
      },
    );
  };

  isRequestApproved = (): boolean => {
    const approvedStates = ["approved", "paid", "returned", "recalled", "reissued", "reversed"];
    return approvedStates.includes(this.state.redemptionRequest?.state || "");
  };

  render(): JSX.Element {
    const {
      isLoading,
      redemptionRequest,
      products,
      isSearching,
      customers,
      selectedCustomer,
      selectedCompany,
      contactTypeFieldsToHide,
      vestingRuleSectionFieldsToHide,
      redemptionFieldsToHide,
      redemptionRequestChecklist,
      isCreateRedemptionBtnDisabled,
      selectedChecklist,
      redemptionPurposes,
      redemptionRequestSubPurposes,
      showConfirmCreateModal,
      vestingRuleFieldsToHide,
      taxWaiverFieldsToHide,
      payoutAccountFieldsToHide,
      payoutFinancialInstitutionId,
      selectedProduct,
      category,
      users,
      manualAllocationFieldsToHide,
      isManualAllocationChecked,
      isFormerCompany,
      savePayoutAccount,
      creatingNewPayoutAccount,
      paymentAccountType,
      accountValidationResponse,
      accountNumber,
      institutionOptions,
      payoutBranches,
      accountName,
      customerModifiedAccounts,
      selectedPayoutAccount,
      paymentMode,
      isCheque,
      unvestedEmployerPreTax,
      unvestedEmployerPostTax,
      unvestedUnassigned,
      requestorTypeFieldsToHide,
      bypassValidation,
    } = this.state;

    const modifiedProducts: Product[] = injectKey<Product>(products, "name");
    const customerAssociatedCompanies: SelectOptionsInterface[] =
      selectedCustomer?.employers?.map((associatedCompany) => {
        return {
          id: associatedCompany.id,
          name: associatedCompany.name,
        };
      }) || [];

    const customerAssociatedFormerCompanies: SelectOptionsInterface[] =
      selectedCustomer?.formerCompanies?.map((associatedCompany) => {
        return {
          id: associatedCompany.id,
          name: associatedCompany.name,
        };
      }) || [];

    const nonEditableStates = ["approved", "reversed", "paid"];
    const disableInput = nonEditableStates.includes(
      redemptionRequest?.state ?? "",
    );

    const config: FormFieldConfig[] = [
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder: "Seach by name or petraId",
        rule: "string",
        label: "Customer",
        disabled: disableInput,
        selectOptions: redemptionRequest?.customer
          ? convertCustomersToSelectOptions([redemptionRequest?.customer])
          : convertCustomersToSelectOptions(customers),
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCustomerSelect,
        initialValue: redemptionRequest?.customer?.id,
        value: redemptionRequest?.customer?.id,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "isFormerCompany",
        name: "isFormerCompany",
        required: false,
        inputType: "checkbox",
        rule: "boolean",
        label: "Is Former Company ?",
        checkboxCheckedValue: isFormerCompany,
        checkboxChangeHandler: (e) =>
          this.changeIsFormerCompany(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "companyId",
        name: "companyId",
        inputType: "select",
        required: true,
        placeholder: "Select customer associated company",
        rule: "string",
        label: "Company",
        selectOptions: generateFirstSelectOption("Select Assignee").concat(
          isFormerCompany
            ? customerAssociatedFormerCompanies
            : customerAssociatedCompanies,
        ),
        disabled: disableInput,
        selectHandler: this.handleCompanySelect,
        changeHandler: this.handleCompanySelect,
        initialValue: redemptionRequest?.company?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "subCategoryId",
        name: "subCategoryId",
        rule: "string",
        inputType: "select",
        required: true,
        disabled: disableInput,
        label: "Request Type",
        placeholder: "Request Type",
        selectOptions: category?.subCategories,
        selectHandler: this.handleRequestTypeSelect,
        initialValue: redemptionRequest?.subCategory?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "userId",
        name: "userId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Assigned To",
        placeholder: "Assigned To",
        disabled: disableInput,
        selectOptions: users,
        initialValue: redemptionRequest?.user?.id,
        changeHandler: this.handleUserSearchInput,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionType",
        name: "redemptionType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Redemption Type",
        placeholder: "Redemption Type",
        disabled: disableInput,
        value: this.state.redemptionType,
        changeHandler: this.handleRedemptionTypeChange,
        selectOptions: generateRedemptionTypes(),
        initialValue:
          redemptionTypes[redemptionRequest?.redemptionType ?? "amount"],
        colConfig: {
          span: 24,
        },
      },
      {
        name: "redemptionValue",
        rule: "number",
        inputType: "number",
        required: true,
        disabled: disableInput,
        label: this.generateRedemptionText(),
        placeholder: this.generateRedemptionText(),
        elementStyle: { width: "100%" },
        initialValue: redemptionRequest?.redemptionValue,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "totalAmountType",
        name: "totalAmountType",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Total Amount Type",
        placeholder: "Select Redemption Total Amount Type",
        required: true,
        disabled: disableInput,
        selectOptions: [
          { id: "gross", name: "Gross" },
          { id: "net", name: "Net" },
        ],
        initialValue: this.state.initialTotalAmountType,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "customerSubscriptionId",
        name: "customerSubscriptionId",
        inputType: "select",
        rule: "string",
        label: "Product",
        placeholder: "Select Product",
        required: true,
        disabled: disableInput,
        changeHandler: this.handleProductSelect,
        selectHandler: this.handleProductSelect,
        selectOptions: modifiedProducts,
        initialValue: redemptionRequest?.customerSubscription?.product?.name,
        value: redemptionRequest?.customerSubscription?.product?.name,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "paymentMode",
        name: "paymentMode",
        placeholder: "Payment Mode",
        initialValue: paymentMode,
        inputType: "select",
        rule: "string",
        label: "Payment Mode",
        disabled: disableInput,
        selectHandler: this.handlePaymentModeSelect,
        selectOptions: [
          {
            id: PaymentModeTypesEnum.cheque,
            name: paymentModeTypes.cheque,
            value: PaymentModeTypesEnum.cheque,
          },
          {
            id: PaymentModeTypesEnum.bankTransfer,
            name: paymentModeTypes.bank_transfer,
            value: PaymentModeTypesEnum.bankTransfer,
          },
        ],
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "payoutAccountId",
        name: "payoutAccountId",
        inputType: "select",
        rule: "string",
        label: "Payout Account",
        placeholder: "Select Payout Account",
        required: false,
        disabled: disableInput,
        selectOptions: customerModifiedAccounts,
        selectHandler: this.handlePayoutAccountSelect,
        initialValue: selectedPayoutAccount,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountType",
        name: "accountType",
        placeholder: "Type",
        inputType: "select",
        rule: "string",
        selectHandler: this.handlePaymentTypeSelect,
        changeHandler: this.handlePaymentTypeChange,
        initialValue: paymentAccountType,
        label: "Payment Account Type",
        disabled: disableInput,
        selectOptions: [
          {
            id: PaymentAccountTypesEnum.cheque,
            name: paymentAccountTypes.cheque,
            value: PaymentAccountTypesEnum.cheque,
          },
          {
            id: PaymentAccountTypesEnum.bankAccount,
            name: paymentAccountTypes.bank_account,
            value: PaymentAccountTypesEnum.bankAccount,
          },
        ],
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "financialInstitutionId",
        name: "financialInstitutionId",
        placeholder: "Institution Name",
        inputType: "select",
        selectHandler: this.handlePayoutFinancialInstitutionChange,
        selectOptions: institutionOptions,
        rule: "string",
        initialValue: payoutFinancialInstitutionId,
        label: "Institution Name",
        required: !isCheque,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountNumber",
        name: "accountNumber",
        placeholder: "Account Number",
        inputType: "string",
        rule: "string",
        label: "Account Number",
        defaultValue: accountNumber,
        onChange: this.handleAccountNumberChange,
        required: !isCheque,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountName",
        name: "accountName",
        inputType: isCheque ? "string" : "description",
        rule: "string",
        required: true,
        placeholder: isCheque ? "Account Name" : "",
        label: "Account Name",
        onChange: this.handleAccountNameChange,
        value: accountValidationResponse?.name ?? accountName,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "verifyPayoutAccount",
        name: "verifyPayoutAccount",
        inputType: "button",
        className: "base-button",
        required: false,
        handleClick: this.verifyAccount,
        value: "Verify",
        elementStyle: {
          position: "relative",
          textAlign: "left",
        },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionRequestPurposeId",
        name: "redemptionRequestPurposeId",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Purpose",
        placeholder: "Select Redemption Purpose",
        required: true,
        disabled: disableInput,
        changeHandler: this.handleRedemptionPurposeChange,
        selectOptions: redemptionPurposes,
        initialValue: redemptionRequest?.redemptionRequestPurpose?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "bypassValidation",
        name: "bypassValidation",
        inputType: "checkbox",
        required: false,
        rule: "boolean",
        label: "Third Party?",
        checkboxCheckedValue: bypassValidation,
        checkboxChangeHandler: (e) => this.handleBypassValidationChange(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "savePayoutAccount",
        name: "savePayoutAccount",
        rule: "boolean",
        inputType: "checkbox",
        required: false,
        label: "Save Payout Account?",
        checkboxCheckedValue: savePayoutAccount,
        disabled: bypassValidation,
        checkboxChangeHandler: (e) =>
          this.toggleSavePayoutAccount(e.target.checked),
        colConfig: {
          span: 7,
        },
      },
      {
        id: "bankBranchId",
        name: "bankBranchId",
        placeholder: "Branch",
        inputType: "select",
        rule: "string",
        label: "Branch",
        selectOptions: payoutBranches,
        changeHandler: this.handleBankBranchChange,
        required: true,
        disabled: disableInput,
        initialValue: creatingNewPayoutAccount
          ? ""
          : redemptionRequest?.payoutAccount?.bankBranch?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        name: "contactType",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Contact Type",
        placeholder: "Contact Type",
        disabled: false,
        changeHandler: this.handleContactTypeChange,
        selectOptions: generateContactTypes(),
        initialValue: redemptionRequest?.contactInformation
          ? isEmail(redemptionRequest?.contactInformation || "")
            ? "Email"
            : "Phone"
          : "",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "email",
        name: "email",
        inputType: "string",
        rule: "email",
        label: "Redemption notification sent to",
        placeholder: "Enter email address ...",
        required: false,
        initialValue: redemptionRequest?.contactInformation
          ? redemptionRequest?.contactInformation
          : this.getPrimaryEmail(),
        colConfig: {
          span: 24,
        },
      },
      {
        id: "phone",
        name: "phone",
        inputType: "phone",
        rule: "string",
        label: "Redemption notification sent to",
        placeholder: "Enter phone number",
        initialValue: redemptionRequest?.contactInformation
          ? redemptionRequest?.contactInformation
          : this.getPrimaryPhone(),
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "clientContactInformation",
        name: "clientContactInformation",
        inputType: "string",
        rule: "email",
        label: "Client redemption notification sent to (Email)",
        placeholder: "Enter email address...",
        initialValue: redemptionRequest?.clientContactInformation,
        required: redemptionRequest?.initiator === "company",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionRequestPurposeId",
        name: "redemptionRequestPurposeId",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Purpose",
        placeholder: "Select Redemption Purpose",
        required: true,
        disabled: disableInput,
        changeHandler: this.handleRedemptionPurposeChange,
        selectOptions: redemptionPurposes,
        initialValue: redemptionRequest?.redemptionRequestPurpose?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "additionalPurposeDescription",
        name: "additionalPurposeDescription",
        inputType: "string",
        rule: "string",
        label: "Purpose Description",
        placeholder: "Purpose description",
        required: true,
        initialValue:
          redemptionRequest?.additionalData?.["additional_purpose_description"],
        colConfig: {
          span: 24,
        },
      },
      {
        id: "redemptionRequestSubPurposeId",
        name: "redemptionRequestSubPurposeId",
        inputType: "select",
        rule: "string",
        label: "Select Redemption Sub Purpose",
        placeholder: "Select Redemption Sub Purpose",
        required: false,
        disabled: disableInput,
        selectOptions: redemptionRequestSubPurposes,
        initialValue:
          redemptionRequest?.redemptionRequestSubPurpose
            ?.redemptionRequestSubPurposeItem?.id,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "idTypeId",
        name: "idTypeId",
        inputType: "select",
        rule: "string",
        required: true,
        disabled: true,
        placeholder: "Ghana Card",
        selectOptions: this.state.idTypes,
        label: "ID Type",
        initialValue: redemptionRequest?.idType?.id || "5",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "idNumber",
        name: "idNumber",
        inputType: "string",
        rule: "string",
        required: true,
        disabled: disableInput,
        placeholder: "ID Number",
        label: "ID Number",
        initialValue: redemptionRequest?.idNumber,
        pattern: ghanaCardRegex,
        patternErrorMessage: "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
        colConfig: {
          span: 24,
        },
      },
      // Temporarily disabled for Tier 3 redemptions - defaults to "No" on the backend (see createRedemptionRequest)
      // {
      //   name: "includeVestingRule",
      //   rule: "string",
      //   inputType: "select",
      //   required: true,
      //   label: "Override Vesting Rule?",
      //   placeholder: "Override Vesting Rule?",
      //   value: "",
      //   changeHandler: this.handleVestingRuleChange,
      //   selectOptions: [
      //     { id: "yes", name: "Yes" },
      //     { id: "no", name: "No" },
      //   ],
      //   initialValue: redemptionRequest?.vestingValue,
      //   colConfig: {
      //     span: 24,
      //   },
      // },
      {
        name: "vestingValueType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Select Vesting Value Type",
        placeholder: "Select Vesting Value Type",
        value: this.state.vestingRuleType,
        changeHandler: this.handleVestingRuleTypeChange,
        selectOptions: [
          { id: "percentage", name: "Percentage" },
          { id: "value", name: "Value" },
        ],
        // initialValue: redemptionRequest?.vestingValueType,
        colConfig: {
          span: 24,
        },
      },
      {
        name: "vestingValue",
        rule: "number",
        inputType: "number",
        required: true,
        label: this.generateVestingRuleText(),
        placeholder: this.generateVestingRuleText(),
        elementStyle: { width: "100%" },
        initialValue: redemptionRequest?.vestingValue,
        colConfig: {
          span: 24,
        },
      },
      {
        name: "vestingRuleUrl",
        label: "Vesting Rule File Upload",
        inputType: "fileUpload",
        defaultFileList: [],
        placeholder: "Vesting Rule File Upload",
        rule: "string",
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("vestingRuleUrl", combinedInfo),
        required: true,
        colConfig: {
          span: 24,
        },
      },
      {
        name: "waiveOffTax",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Should Tax Be Waived Off?",
        placeholder: "Should Tax Be Waived Off?",
        changeHandler: this.handleTaxWaiveChange,
        selectOptions: [
          { id: "yes", name: "Yes" },
          { id: "no", name: "No" },
        ],
        initialValue: redemptionRequest?.taxWaiverNpraUrl || redemptionRequest?.waiveOffTax ? "yes" : "no",
        colConfig: {
          span: 24,
        },
      },
      {
        name: "taxWaiverNpraUrl",
        label: "Upload Contribution Verification Document",
        inputType: "fileUpload",
        defaultFileList: [],
        placeholder: "Upload Contribution Verification Document",
        rule: "string",
        required: false,
        changeHandler: (combinedInfo: string) =>
          this.handleFileUploadChange("taxWaiverNpraUrl", combinedInfo),
        initialValue: redemptionRequest?.taxWaiverNpraUrl,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "correctInfoDate",
        name: "correctInfoDate",
        inputType: "defaultDate",
        required: false,
        rule: "object",
        label: "Correct Info Date",
        initialValue: this.state.redemptionRequest?.correctInfoDate
          ? moment(this.state.redemptionRequest?.correctInfoDate)
          : "",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "requestDate",
        name: "requestDate",
        inputType: "defaultDate",
        required: true,
        rule: "object",
        label: "Request Date",
        disabled: disableInput,
        initialValue: this.getRequestDate(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
    ];

    const rightFormConfig: FormFieldConfig[] = [
      {
        id: "unvestedEmployerPreTax",
        name: "unvestedEmployerPreTax",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Unvested Employer Pre Tax",
        onNumberChange: (e) =>
          this.handleInputChange("unvestedEmployerPreTax", e),
        value: unvestedEmployerPreTax?.toString(),
        placeholder: unvestedEmployerPreTax?.toString(),
        disabled: this.isRequestApproved(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "unvestedEmployerPostTax",
        name: "unvestedEmployerPostTax",
        required: false,
        inputType: "number",
        label: "Unvested Employer Post Tax",
        rule: "number",
        disabled: this.isRequestApproved(),
        value: unvestedEmployerPostTax?.toString(),
        onNumberChange: (e) =>
          this.handleInputChange("unvestedEmployerPostTax", e),
        placeholder: unvestedEmployerPostTax?.toString(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "unvestedUnassigned",
        name: "unvestedUnassigned",
        required: false,
        inputType: "description",
        disabled: true,
        value: unvestedUnassigned?.toString(),
        rule: "number",
        label: "Unvested Unassigned",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "isManualAllocation",
        name: "isManualAllocation",
        required: false,
        inputType: "checkbox",
        rule: "boolean",
        label: "Manual Allocation",
        checkboxCheckedValue: isManualAllocationChecked,
        checkboxChangeHandler: (e) =>
          this.changeIsManualAllocation(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "manualContribution",
        name: "manualContribution",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Manual Contribution",
        initialValue: redemptionRequest?.manualContribution,
        placeholder: "Manual Contribution",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "manualEmployerPreTax",
        name: "manualEmployerPreTax",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Manual Employer Pre Tax",
        initialValue: redemptionRequest?.manualEmployerPreTax,
        placeholder: "Manual Employer Pre Tax",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "manualEmployerPostTax",
        name: "manualEmployerPostTax",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Manual Employer Post Tax",
        initialValue: redemptionRequest?.manualEmployerPostTax,
        placeholder: "Manual Employer Post Tax",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "manualEmployeePreTax",
        name: "manualEmployeePreTax",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Manual Employee Pre Tax",
        initialValue: redemptionRequest?.manualEmployeePreTax,
        placeholder: "Manual Employee Pre Tax",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "manualEmployeePostTax",
        name: "manualEmployeePostTax",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Manual Employee Post Tax",
        initialValue: redemptionRequest?.manualEmployeePostTax,
        placeholder: "Manual Employee Post Tax",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
      {
        id: "manualWithholdingTax",
        name: "manualWithholdingTax",
        required: false,
        inputType: "number",
        rule: "number",
        label: "Manual Withholding Tax",
        initialValue: redemptionRequest?.manualWithholdingTax,
        placeholder: "Manual Withholding Tax",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 13,
        },
      },
    ];

    const options = redemptionRequestChecklist?.map((checklist) => {
      return {
        value: checklist.name,
        label: this.renderLabel(checklist.isRequired, checklist.name),
      };
    });

    return (
      <Fragment>
        {showConfirmCreateModal &&
          ModalCreator({
            title: "Create Redemption Request",
            okType: "primary",
            okText: "Create",
            showModal: showConfirmCreateModal,
            onOkay: this.updateRedemptionRequest,
            toggleModal: this.toggleModal,
            inscription:
              "A pending redemption request exists for this customer and product subscription. Are you sure you want to continue?",
          })}
        {redemptionRequest && <Spin spinning={isLoading}>
          <Card header="New Redemption Request">
            <Row>
              <Col span={8}>
                <BaseForm
                  reference={this.reference}
                  fieldsToHide={[
                    ...contactTypeFieldsToHide,
                    ...redemptionFieldsToHide,
                    ...vestingRuleFieldsToHide,
                    ...taxWaiverFieldsToHide,
                    ...payoutAccountFieldsToHide,
                    ...vestingRuleSectionFieldsToHide,
                    ...requestorTypeFieldsToHide,
                  ]}
                  layout="vertical"
                  fields={config}
                />
              </Col>
              <Col offset={1} span={15}>
                <BaseForm
                  reference={this.rightFormReference}
                  fieldsToHide={[...manualAllocationFieldsToHide]}
                  layout="vertical"
                  fields={rightFormConfig}
                />

                {selectedCustomer && selectedProduct?.fundCode && (
                  <CustomerBalanceBreakdown
                    customerPetraId={selectedCustomer.petraId}
                    fundCode={selectedProduct.fundCode}
                    clientPetraId={selectedCompany?.petraId ?? redemptionRequest?.company?.petraId}
                    priceDate={redemptionRequest?.valueDate}
                    showTitle={true}
                    showPendingRedemptionsCard={true}
                    compact={false}
                  />
                )}

                <hr></hr>

                {selectedProduct && (
                  <Descriptions
                    layout={"horizontal"}
                    style={{ marginTop: "10px" }}
                  >
                    <Descriptions.Item
                      label={<b>Product Duration( {selectedProduct.name})</b>}
                    >
                      {selectedProduct.productDuration}
                    </Descriptions.Item>
                  </Descriptions>
                )}
                <hr></hr>

                {redemptionRequestChecklist.length > 0 && (
                  <div>
                    <h3>Redemption Checklist</h3>
                    <Checkbox.Group
                      options={options}
                      value={selectedChecklist}
                      onChange={this.toggleRedemptionBtnVisible}
                    ></Checkbox.Group>
                    <div>
                      <Button
                        type="primary"
                        disabled={isCreateRedemptionBtnDisabled}
                        onClick={this.toggleConfirmCreateModal}
                        className="base-button"
                        style={{
                          position: "relative",
                          top: "10px",
                          right: "5px",
                          borderRadius: 0,
                          height: "33px",
                        }}
                      >
                        Update Redemption Request
                      </Button>
                      <Button
                        type="primary"
                        onClick={this.toggleCancelModal}
                        className="base-button"
                        style={{
                          position: "relative",
                          top: "10px",
                          borderRadius: 0,
                          height: "33px",
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    {this.state.showCancelRequest &&
                      ModalCreator({
                        title: "Cancel",
                        okType: "primary",
                        okText: "Yes",
                        width: 400,
                        showModal: true,
                        onOkay: this.cancelRequest,
                        toggleModal: this.toggleCancelModal,
                        showFooter: true,
                        inscription: `Are you sure you want to cancel request ?`,
                      })}
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </Spin>}
      </Fragment>
    );
  }
}

export default withRouter(EditRedemptionRequest);
